import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost, ApiError } from "../lib/api";

type Plan = { id: number | string; label: string };
type PlansResp = { plans: Plan[]; current_plan_id?: number | string | null };

export default function AccountsChangePlan() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiGet<PlansResp>(`/xapi/accounts/${id}/plans`);
        if (!mounted) return;
        if (data?.plans?.length) {
          setPlans(data.plans);
          setSelected(String(data.current_plan_id ?? data.plans[0].id));
        }
      } catch (e: any) {
        if (e instanceof ApiError) setError(e.message);
        else setError("Failed to load plans.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await apiPost(`/xapi/accounts/${id}/change-plan`, { plan_id: selected });
      navigate("/accounts");
      return;
    } catch (e: any) {
      setError(e?.message || "Could not save change.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-xl mx-auto">
          <h1 className="text-2xl font-semibold mb-4">Change plan</h1>
          <div className="card-box">
            <div className="skeleton h-4 w-1/2 mb-2 rounded" />
            <div className="skeleton h-10 w-full rounded" />
          </div>
        </div>
      </div>
    );
  }

  const noJson = !plans.length;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-semibold mb-4">Change plan</h1>

        {noJson ? (
          <div className="section-box p-4">
            <p className="text-sm text-gray-600">
              This screen uses the server page for now.
            </p>
            <a
              className="btn btn-white mt-3"
              href={`/accounts/${id}/change-plan`}
            >
              Open server page
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="form-section">
            {error && <div className="alert-danger">{error}</div>}

            <div>
              <label className="block text-sm font-medium mb-1">Select plan</label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                {plans.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="btn"
              >
                {submitting ? "Saving…" : "Save change"}
              </button>
              <Link to="/accounts" className="nav-link">Cancel</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
